import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { HelpersService } from '../../../../services/helpers.service';
import { ReceiptTemplateService } from './services/receipt-template.service';
import { ReceiptTemplate, TemplateType } from './models/receipt-template.model';

@Component({
  selector: 'app-receipt-template-designer',
  templateUrl: './receipt-template-designer.component.html',
  styleUrls: ['./receipt-template-designer.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class ReceiptTemplateDesignerComponent implements OnInit, OnDestroy {

  public templates: ReceiptTemplate[] = [];
  public filteredTemplates: ReceiptTemplate[] = [];
  public loading = false;
  public searchText = '';
  public selectedTypeFilter: TemplateType | 'ALL' = 'ALL';

  public typeFilterOptions: { label: string; value: TemplateType | 'ALL' }[] = [];

  public get columns() {
    return [
      { header: this.translate.instant('bikeshop.receipt_template.list.col_name'),         field: 'name',            type: 'string'  },
      { header: this.translate.instant('bikeshop.receipt_template.list.col_type'),         field: 'type',            type: 'type'    },
      { header: this.translate.instant('bikeshop.receipt_template.list.col_dimensions'),   field: 'dimensions',      type: 'custom'  },
      { header: this.translate.instant('bikeshop.receipt_template.list.col_active'),       field: 'isActive',        type: 'boolean' },
      { header: this.translate.instant('bikeshop.receipt_template.list.col_default'),      field: 'isGlobalDefault', type: 'boolean' },
      { header: this.translate.instant('bikeshop.receipt_template.list.col_last_updated'), field: 'updatedAt',       type: 'date'    },
    ];
  }

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private helpersService: HelpersService,
    private templateService: ReceiptTemplateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.buildTypeFilterOptions();
    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Computed ────────────────────────────────────────────────────────────────

  get isThirdPartyModuleEnabled(): boolean {
    const products = this.helpersService.getPartnerData()?.SubscribedProducts ?? [];
    return products.some((p: any) =>
      ['THIRDPARTY', 'THIRD_PARTY', 'DISPATCH'].includes(p.ProductType) &&
      [1, 3, 4, 20, 50, 51].includes(p.StatusID),
    );
  }

  getDimensions(template: ReceiptTemplate): string {
    return `${template.widthMm} × ${template.heightMm} mm`;
  }

  getTypeLabel(type: TemplateType): string {
    return type === 'JOB_CARD'
      ? this.translate.instant('bikeshop.receipt_template.list.type_job_card')
      : this.translate.instant('bikeshop.receipt_template.list.type_dispatch');
  }

  // ─── Data ────────────────────────────────────────────────────────────────────

  private buildTypeFilterOptions(): void {
    this.typeFilterOptions = [
      { label: this.translate.instant('bikeshop.receipt_template.list.type_all'),      value: 'ALL'      },
      { label: this.translate.instant('bikeshop.receipt_template.list.type_job_card'), value: 'JOB_CARD' },
    ];
    if (this.isThirdPartyModuleEnabled) {
      this.typeFilterOptions.push({ label: this.translate.instant('bikeshop.receipt_template.list.type_dispatch'), value: 'THIRD_PARTY_DISPATCH' });
    }
  }

  private loadTemplates(): void {
    this.loading = true;

    // Subscribe to the reactive cache for live UI updates.
    this.templateService.getTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe(templates => {
        this.templates = templates;
        this.applyFilter();
      });

    // Trigger the actual HTTP call; clear loading spinner when it completes.
    this.templateService.refresh()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.loading = false; });
  }

  // ─── Filtering ───────────────────────────────────────────────────────────────

  applyFilter(): void {
    let result = [...this.templates];
    if (this.selectedTypeFilter !== 'ALL') {
      result = result.filter(t => t.type === this.selectedTypeFilter);
    }
    if (this.searchText.trim()) {
      const q = this.searchText.toLowerCase();
      result = result.filter(t => t.name.toLowerCase().includes(q));
    }
    this.filteredTemplates = result;
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  createNew(): void {
    this.router.navigate(['/store/receipt-templates/new']);
  }

  editTemplate(template: ReceiptTemplate): void {
    this.router.navigate(['/store/receipt-templates/editor', template.id]);
  }

  duplicateTemplate(template: ReceiptTemplate): void {
    this.templateService.duplicateTemplate(template.id).subscribe(copy => {
      if (copy) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('bikeshop.receipt_template.list.toast_duplicated_summary'),
          detail: this.translate.instant('bikeshop.receipt_template.list.toast_duplicated_detail', { name: copy.name }),
        });
      }
    });
  }

  exportTemplate(template: ReceiptTemplate): void {
    this.templateService.exportTemplate(template);
  }

  deleteTemplate(template: ReceiptTemplate): void {
    this.confirmationService.confirm({
      message: this.translate.instant('bikeshop.receipt_template.list.confirm_delete_message', { name: template.name }),
      header:  this.translate.instant('bikeshop.receipt_template.list.confirm_delete_header'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.templateService.deleteTemplate(template.id).subscribe(() => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('bikeshop.receipt_template.list.toast_deleted_summary'),
            detail: this.translate.instant('bikeshop.receipt_template.list.toast_deleted_detail', { name: template.name }),
          });
        });
      },
    });
  }

  onActiveChange(template: ReceiptTemplate, event: { checked: boolean }): void {
    if (event.checked) {
      // Activating: deactivate all other templates of the same type.
      this.templateService.setActive(template.id, template.type).subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('bikeshop.receipt_template.list.toast_activated_summary'),
          detail: this.translate.instant('bikeshop.receipt_template.list.toast_activated_detail', { name: template.name, type: this.getTypeLabel(template.type) }),
        });
      });
    } else {
      // Deactivating: just save this template with isActive false.
      this.templateService.saveTemplate({ ...template, isActive: false }).subscribe(() => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('bikeshop.receipt_template.list.toast_deactivated_summary'),
          detail: this.translate.instant('bikeshop.receipt_template.list.toast_deactivated_detail', { name: template.name }),
        });
      });
    }
  }

  onDefaultChange(template: ReceiptTemplate, event: { checked: boolean }): void {
    if (event.checked) {
      // Setting as default: clear default from all other templates of the same type.
      this.templateService.setGlobalDefault(template.id, template.type).subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('bikeshop.receipt_template.list.toast_default_set_summary'),
          detail: this.translate.instant('bikeshop.receipt_template.list.toast_default_set_detail', { name: template.name, type: this.getTypeLabel(template.type) }),
        });
      });
    } else {
      // Clearing the default flag.
      this.templateService.saveTemplate({ ...template, isGlobalDefault: false }).subscribe(() => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('bikeshop.receipt_template.list.toast_default_cleared_summary'),
          detail: this.translate.instant('bikeshop.receipt_template.list.toast_default_cleared_detail', { name: template.name }),
        });
      });
    }
  }

  onFileImport(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        this.templateService.importTemplate(e.target?.result as string).subscribe(() => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('bikeshop.receipt_template.list.toast_imported_summary'),
            detail: this.translate.instant('bikeshop.receipt_template.list.toast_imported_detail'),
          });
        });
      } catch {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('bikeshop.receipt_template.list.toast_import_failed_summary'),
          detail: this.translate.instant('bikeshop.receipt_template.list.toast_import_failed_detail'),
        });
      }
    };
    reader.readAsText(file);
    input.value = '';
  }
}
